/* eslint-disable react/prop-types */
import {Component, MouseEvent, ReactNode} from 'react';

/**
 * Shared modal/dialog primitive for the portal.
 *
 * Centralises the backdrop + dialog wrapper pattern used by every
 * confirm/edit/create dialog under `/portal/*`. Callers retain
 * control over the visual styling (each consumer typically owns its
 * own `.dialog` SCSS block with content-shape rules like form-input
 * paddings), and the primitive owns the behavioural concerns:
 *
 * - Backdrop click triggers [onClose] unless [closeOnBackdropClick]
 *   is `false` (typically wired to a "busy" flag while a network
 *   call is in flight, so the user can't accidentally close mid-op).
 * - The Escape key triggers [onClose] unless [closeOnEsc] is `false`.
 * - role="dialog" + aria-modal="true" stamped on the inner element.
 * - Clicks inside the dialog body do NOT bubble to the backdrop, so
 *   selecting input text or clicking buttons never closes the modal.
 *
 * Migration target for §5.F "extract <PortalModal> primitive" quick
 * win. The existing per-component dialog SCSS modules continue to
 * carry their content rules (h3 margin, input paddings, button row
 * etc.) — those don't belong in a shell primitive.
 */
interface PortalModalProps {
    /**
     * Invoked when the user dismisses the modal. The caller decides
     * whether to actually unmount the modal in response.
     */
    onClose: () => void;
    /**
     * When `false`, backdrop clicks are suppressed. Default `true`.
     * Wire to a busy-state flag if the modal hosts a destructive
     * action mid-flight.
     */
    closeOnBackdropClick?: boolean;
    /** When `false`, Escape-key handler is disabled. Default `true`. */
    closeOnEsc?: boolean;
    /**
     * Class for the fixed-position backdrop element. Callers pass
     * their existing `styles.dialogBackdrop` so visual styling stays
     * with the consumer's SCSS module.
     */
    backdropClassName: string;
    /** Class for the inner dialog element. */
    dialogClassName: string;
    /** Optional accessible label when the dialog has no visible heading. */
    'aria-label'?: string;
    /** Optional id reference to a visible heading element. */
    'aria-labelledby'?: string;
    /** Optional test-id stamped on the backdrop, useful in Playwright. */
    dataTestId?: string;
    children: ReactNode;
}

export default class PortalModal extends Component<PortalModalProps> {
    componentDidMount(): void {
        if (this.props.closeOnEsc !== false && typeof document !== 'undefined') {
            document.addEventListener('keydown', this.handleKeyDown);
        }
    }

    componentWillUnmount(): void {
        if (typeof document !== 'undefined') {
            document.removeEventListener('keydown', this.handleKeyDown);
        }
    }

    private handleKeyDown = (e: KeyboardEvent): void => {
        if (e.key === 'Escape') {
            this.props.onClose();
        }
    };

    private handleBackdropClick = (): void => {
        if (this.props.closeOnBackdropClick === false) return;
        this.props.onClose();
    };

    private stopPropagation = (e: MouseEvent): void => {
        e.stopPropagation();
    };

    render(): ReactNode {
        const {
            backdropClassName,
            dialogClassName,
            dataTestId,
            children,
        } = this.props;
        return (
            <div
                className={backdropClassName}
                onClick={this.handleBackdropClick}
                data-testid={dataTestId}
            >
                <div
                    className={dialogClassName}
                    role="dialog"
                    aria-modal="true"
                    aria-label={this.props['aria-label']}
                    aria-labelledby={this.props['aria-labelledby']}
                    onClick={this.stopPropagation}
                >
                    {children}
                </div>
            </div>
        );
    }
}
